import {
  type ModalController,
  type ModalHookContext,
  setupModalCommands,
  syncModalAccessibleName
} from './modal'

// Private types ---------------------------------------------------------------

type DrawerHookContext = ModalHookContext & {
  drawerCleanup?: () => void
  modalController?: ModalController
}

// Constants -------------------------------------------------------------------

const COMBOBOX_SELECTOR = 'wa-combobox, [role="combobox"]'

// Private functions -----------------------------------------------------------

// Accepts an unmodified Enter key for implicit form submission.
const isEnterWithoutModifiers = (event: KeyboardEvent): boolean =>
  event.key === 'Enter' &&
  !event.shiftKey &&
  !event.ctrlKey &&
  !event.metaKey &&
  !event.altKey

// Excludes controls that already own Enter behavior.
const skipsImplicitSubmit = (target: HTMLElement): boolean =>
  target.tagName === 'TEXTAREA' ||
  target.tagName === 'BUTTON' ||
  target.getAttribute('role') === 'button'

// Detects keyboard events originating from a combobox interaction.
const comesFromCombobox = (event: KeyboardEvent): boolean =>
  event
    .composedPath()
    .some(
      (target) =>
        target instanceof HTMLElement && target.matches(COMBOBOX_SELECTOR)
    )

// Submits through the modern API with a native-event fallback.
const submitForm = (form: HTMLFormElement): void => {
  if (typeof form.requestSubmit === 'function') {
    form.requestSubmit()
    return
  }

  const submitEvent = new Event('submit', {
    bubbles: true,
    cancelable: true
  })

  if (form.dispatchEvent(submitEvent)) {
    form.submit()
  }
}

// Adds the drawer's implicit Enter-to-submit behavior.
const setupEnterSubmit = (drawer: HTMLElement): (() => void) => {
  const handleKeyDown = (event: KeyboardEvent): void => {
    if (
      !(isEnterWithoutModifiers(event) && event.target instanceof HTMLElement)
    ) {
      return
    }

    if (skipsImplicitSubmit(event.target) || comesFromCombobox(event)) {
      return
    }

    const form = event.target.closest<HTMLFormElement>('form')

    if (form === null) {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    submitForm(form)
  }

  drawer.addEventListener('keydown', handleKeyDown, true)

  return () => drawer.removeEventListener('keydown', handleKeyDown, true)
}

// Public hook -----------------------------------------------------------------

export const FacadeDrawer = {
  // Creates modal and Enter-key controllers for this drawer.
  mounted(this: DrawerHookContext) {
    this.drawerCleanup?.()

    const modalController = setupModalCommands(this, 'drawer')
    const enterCleanup = setupEnterSubmit(this.el)

    this.modalController = modalController
    this.drawerCleanup = () => {
      modalController.cleanup()
      enterCleanup()
    }
  },

  // Reconciles runtime state after a LiveView patch.
  updated(this: DrawerHookContext) {
    this.modalController?.sync()
    syncModalAccessibleName(this.el)
  },

  // Releases every controller owned by this hook.
  destroyed(this: DrawerHookContext) {
    this.drawerCleanup?.()
    this.drawerCleanup = undefined
    this.modalController = undefined
  }
}
